import { useEffect } from 'react';
import { CSS_VARIABLE_PREFIX } from '@whinerp/ui';
import type { BrandPalette } from '@whinerp/client';
import { useTheme } from './theme.js';

/**
 * Wears the workspace's colour (F-015).
 *
 * The design system anticipated this: every component styles itself with
 * `var(--w-bronze...)` rather than a compiled value, precisely so a workspace
 * can override the accent at runtime. This is the six lines that do it.
 *
 * Re-applied when the resolved theme changes, because the shades are not the
 * same in both: a colour that reads on a near-white page is not the colour that
 * reads on a near-black one, and the palette carries both. Custom properties
 * set inline on the root cannot vary by media query, so the resolved theme has
 * to drive it.
 *
 * Removed, not left behind, when branding is switched off. A workspace that
 * turns it off and sees its old colour until a hard refresh has not really
 * turned it off.
 */
const BRAND_VARIABLES = ['bronze', 'bronze-hover', 'bronze-tint', 'bronze-rule', 'bronze-text', 'bronze-on'];

export function useBrandPalette(palette: BrandPalette | null, enabled: boolean): void {
  const { resolved } = useTheme();

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const clear = (): void => {
      for (const name of BRAND_VARIABLES) root.style.removeProperty(`${CSS_VARIABLE_PREFIX}${name}`);
    };

    if (!enabled || !palette) {
      clear();
      return;
    }

    const dark = resolved === 'dark';
    const values: Record<string, string> = {
      bronze: dark ? palette.markDark : palette.mark,
      'bronze-hover': palette.hover,
      'bronze-tint': palette.tint,
      // The rule is the same colour at low weight: a hairline held to the mark
      // bar would be a border as loud as the button it sits beside.
      'bronze-rule': palette.tint,
      'bronze-text': dark ? palette.textDark : palette.text,
      'bronze-on': palette.onMark,
    };
    for (const [name, value] of Object.entries(values)) {
      root.style.setProperty(`${CSS_VARIABLE_PREFIX}${name}`, value);
    }
    return clear;
  }, [palette, enabled, resolved]);
}
